import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Minimize2, CheckCircle2 } from 'lucide-react';

interface ForecastRefreshProgressModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMinimize: () => void;
  isComplete: boolean;
}

export function ForecastRefreshProgressModal({
  open,
  onOpenChange,
  onMinimize,
  isComplete,
}: ForecastRefreshProgressModalProps) {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    'Recalculating forecasts...',
    'Processing scenarios...',
    'Updating recommendations...',
  ];

  useEffect(() => {
    if (!open || isComplete) return;

    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % steps.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [open, isComplete]);

  useEffect(() => {
    if (isComplete) {
      setCurrentStep(0);
    }
  }, [isComplete]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" hideCloseButton={!isComplete}>
        <DialogHeader>
          <DialogTitle>
            {isComplete ? 'Forecast Refresh Complete' : 'Forecast Refresh in Progress'}
          </DialogTitle>
          {!isComplete && (
            <DialogDescription>
              Please wait while we recalculate your forecasts
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="py-8">
          {isComplete ? (
            <div className="flex flex-col items-center gap-4 text-center">
              <CheckCircle2 className="h-16 w-16 text-green-600" />
              <p className="text-lg font-medium">All forecasts have been updated</p>
              <p className="text-sm text-muted-foreground">
                Your predictions, scenarios, and recommendations are now up to date
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-6 text-center">
              <Loader2 className="h-16 w-16 animate-spin text-teal-600" />
              <div className="space-y-2">
                {steps.map((step, index) => (
                  <p
                    key={step}
                    className={`text-sm transition-opacity ${
                      index === currentStep
                        ? 'font-medium text-foreground'
                        : 'text-muted-foreground opacity-50'
                    }`}
                  >
                    {step}
                  </p>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                This may take several minutes
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          {isComplete ? (
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          ) : (
            <Button variant="outline" onClick={onMinimize}>
              <Minimize2 className="mr-2 h-4 w-4" />
              Minimise
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
